import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Check, Crown, Sparkles, Zap, Clock, Tag, Users, Palette, MessageSquare, Image, QrCode, Globe, Sun, Moon, Map, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Subscription() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language, setLanguage } = useLanguage();
  const { adminDarkMode, toggleAdminDarkMode, adminTheme: th } = useTheme();
  
  const eventId = searchParams.get('event_id');
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherDiscount, setVoucherDiscount] = useState(null);
  const [validatingVoucher, setValidatingVoucher] = useState(false);
  const [trialDays, setTrialDays] = useState(3);

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      const [tiersRes] = await Promise.all([
        axios.get(`${API}/pricing/tiers`)
      ]);
      setTiers(tiersRes.data.tiers);
      setTrialDays(tiersRes.data.trial_days);
      
      if (eventId) {
        const tierInfoRes = await axios.get(`${API}/events/${eventId}/tier-info`);
        setEventInfo(tierInfoRes.data);
      }
    } catch (error) {
      toast.error('Failed to load pricing information');
    } finally {
      setLoading(false);
    }
  };

  const validateVoucher = async () => {
    if (!voucherCode.trim() || !selectedTier) {
      toast.error(t('selectPlanFirst'));
      return;
    }
    
    setValidatingVoucher(true);
    try {
      const response = await axios.post(`${API}/vouchers/validate?voucher_code=${voucherCode}&tier=${selectedTier}`);
      setVoucherDiscount(response.data);
      toast.success(t('voucherApplied'));
    } catch (error) {
      setVoucherDiscount(null);
      toast.error(error.response?.data?.detail || 'Invalid voucher code');
    } finally {
      setValidatingVoucher(false);
    }
  };

  const handleStartTrial = async (tierId) => {
    if (!eventId) {
      toast.error('Please select an event first');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(`${API}/events/${eventId}/start-trial`, {
        event_id: eventId,
        tier: tierId
      });
      toast.success(`${trialDays}-day trial started!`);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trial');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpgrade = async (tierId) => {
    if (!eventId) {
      toast.error('Please select an event first');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        event_id: eventId,
        tier: tierId,
        origin_url: window.location.origin,
        voucher_code: voucherDiscount?.voucher_code || null
      });
      
      // Handle free upgrade (100% discount)
      if (response.data.free_upgrade) {
        toast.success(response.data.message || 'Event upgraded successfully!');
        navigate(`/events/${eventId}`);
        return;
      }
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setProcessing(false);
    }
  };

  const getTierIcon = (tierId) => {
    switch (tierId) {
      case 'free': return <Users className="h-6 w-6" />;
      case 'premium': return <Sparkles className="h-6 w-6" />;
      case 'pro': return <Zap className="h-6 w-6" />;
      default: return null;
    }
  };

  const getTierColor = (tierId) => {
    switch (tierId) {
      case 'free': return 'border-slate-500';
      case 'premium': return 'border-blue-500';
      case 'pro': return 'border-amber-500';
      default: return 'border-slate-700';
    }
  };

  const getTierName = (tierId) => {
    switch (tierId) {
      case 'free': return t('free');
      case 'premium': return t('premium');
      case 'pro': return t('pro');
      default: return tierId;
    }
  };

  const getTierDescription = (tierId) => {
    switch (tierId) {
      case 'free': return t('freeDescription');
      case 'premium': return t('premiumDescription');
      case 'pro': return t('proDescription');
      default: return '';
    }
  };

  const getPrice = (tier) => {
    if (voucherDiscount && selectedTier === tier.id) {
      return {
        base: tier.price,
        discounted: voucherDiscount.discounted_price,
        tax: voucherDiscount.tax_amount,
        total: voucherDiscount.total_amount,
        hasDiscount: true
      };
    }
    return {
      base: tier.price,
      discounted: tier.price,
      tax: tier.price * 0.23,
      total: tier.price_with_tax,
      hasDiscount: false
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen admin-bg flex items-center justify-center transition-colors duration-300">
        <div className="animate-pulse admin-accent">{t('processing')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen admin-bg p-4 md:p-8 transition-colors duration-300">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(eventId ? `/events/${eventId}` : '/dashboard')}
            className="admin-text-secondary"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('back')}
          </Button>
          
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleAdminDarkMode}
              className="p-2 rounded-lg transition-all duration-200 admin-card admin-card-hover admin-icon"
              title={adminDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              data-testid="theme-toggle-btn"
            >
              {adminDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            
            {/* Language Selector */}
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger 
                className="w-[140px] admin-card admin-text"
                data-testid="language-select"
              >
                <Globe className="h-4 w-4 mr-2 admin-icon" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">{t('english')}</SelectItem>
                <SelectItem value="pt">{t('portuguese')}</SelectItem>
                <SelectItem value="fr">{t('french')}</SelectItem>
                <SelectItem value="es">{t('spanish')}</SelectItem>
                <SelectItem value="el">{t('greek')}</SelectItem>
                <SelectItem value="ru">{t('russian')}</SelectItem>
                <SelectItem value="it">{t('italian')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4 admin-accent">
            {t('upgradeEvent')}
          </h1>
          <p className="max-w-2xl mx-auto admin-text-secondary">
            {t('unlockPremiumFeatures')} {t('chooseYourPlan')}
          </p>
          
          {eventInfo && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-slate-900/50 rounded-full border border-slate-700">
              <span className="text-slate-400">{t('currentPlan')}:</span>
              <span className="text-white font-medium">{getTierName(eventInfo.current_tier)}</span>
              {eventInfo.is_trial && (
                <span className="flex items-center gap-1 text-amber-400 text-sm">
                  <Clock className="h-3 w-3" />
                  {t('trial')}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-10 max-w-5xl mx-auto">
          {tiers.filter(tier => tier.id !== 'premium_plus').map((tier) => {
            const price = getPrice(tier);
            const isCurrentTier = eventInfo?.current_tier === tier.id;
            const isFree = tier.id === 'free';
            
            return (
              <div
                key={tier.id}
                className={`glass-card p-6 border-2 transition-all ${
                  selectedTier === tier.id ? getTierColor(tier.id) : 'border-transparent'
                } ${isCurrentTier ? 'ring-2 ring-green-500' : ''} ${isFree ? 'opacity-75' : ''}`}
                onClick={() => !isCurrentTier && !isFree && setSelectedTier(tier.id)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-2 rounded-lg ${
                    tier.id === 'free' ? 'bg-slate-500/20 text-slate-400' :
                    tier.id === 'premium' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-amber-500/20 text-amber-400'
                  }`}>
                    {getTierIcon(tier.id)}
                  </div>
                  {isCurrentTier && (
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                      {t('currentPlan')}
                    </span>
                  )}
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2">{getTierName(tier.id)}</h3>
                <p className="text-slate-400 text-sm mb-4">{getTierDescription(tier.id)}</p>
                
                <div className="mb-6">
                  {price.hasDiscount ? (
                    <>
                      <span className="text-2xl font-bold text-slate-500 line-through mr-2">€{price.base}</span>
                      <span className="text-3xl font-bold text-[#D4AF37]">€{price.discounted}</span>
                    </>
                  ) : (
                    <span className="text-3xl font-bold text-[#D4AF37]">€{tier.price}</span>
                  )}
                  <span className="text-slate-400 text-sm ml-2">+ 23% {t('tax')}</span>
                  <div className="text-slate-500 text-sm">
                    {t('total')}: €{price.total.toFixed(2)}
                  </div>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-slate-300">
                    <Users className="h-4 w-4 text-[#D4AF37]" />
                    <span>{tier.max_guests === -1 ? t('unlimited') : tier.max_guests} {t('guests')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Palette className="h-4 w-4 text-[#D4AF37]" />
                    <span>{t('allColorThemes')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <MessageSquare className="h-4 w-4 text-[#D4AF37]" />
                    <span>{t('customMessages')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Image className="h-4 w-4 text-[#D4AF37]" />
                    <span>{t('backgroundImages')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <QrCode className="h-4 w-4 text-[#D4AF37]" />
                    <span>{t('customQrColor')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Map className="h-4 w-4 text-[#D4AF37]" />
                    <span>Floor Plan Editor</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Heart className="h-4 w-4 text-[#D4AF37]" />
                    <span>Love Gallery</span>
                  </div>
                  {tier.id === 'premium' && (
                    <div className="flex items-center gap-2 text-slate-300">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{t('oneEvent')}</span>
                    </div>
                  )}
                  {tier.id === 'pro' && (
                    <div className="flex items-center gap-2 text-slate-300">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{t('upToEvents').replace('{count}', '3')}</span>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  {isFree ? (
                    <Button
                      className="w-full bg-slate-600 hover:bg-slate-600 cursor-default"
                      disabled={true}
                    >
                      {isCurrentTier ? t('currentPlan') : t('free')}
                    </Button>
                  ) : (
                    <Button
                      className={`w-full ${
                        tier.id === 'premium' ? 'bg-blue-600 hover:bg-blue-700' :
                        'bg-amber-600 hover:bg-amber-700'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTier(tier.id);
                        handleUpgrade(tier.id);
                      }}
                      disabled={processing || isCurrentTier || !eventId}
                    >
                      {isCurrentTier ? t('current') : processing ? t('processing') : t('upgradeNow')}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Voucher Section */}
        <div className="glass-card p-6 max-w-md mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="h-5 w-5 text-[#D4AF37]" />
            <h3 className="text-lg font-semibold text-white">{t('haveVoucher')}</h3>
          </div>
          <div className="flex gap-2">
            <Input
              placeholder={t('enterVoucherCode')}
              value={voucherCode}
              onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
              className="bg-slate-900/50 border-slate-700 uppercase"
            />
            <Button
              onClick={validateVoucher}
              disabled={validatingVoucher || !selectedTier}
              className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
            >
              {validatingVoucher ? '...' : t('apply')}
            </Button>
          </div>
          {!selectedTier && (
            <p className="text-slate-500 text-xs mt-2">{t('selectPlanFirst')}</p>
          )}
          {voucherDiscount && (
            <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-400 text-sm">
                <Check className="inline h-4 w-4 mr-1" />
                {t('voucherApplied')} {t('youSave')} €{voucherDiscount.discount_amount.toFixed(2)}
              </p>
            </div>
          )}
        </div>

        {/* Feature Comparison */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">{t('featureComparison')}</h2>
          <div className="glass-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left p-4 text-slate-400">{t('feature')}</th>
                  <th className="text-center p-4 text-slate-400">{t('free')}</th>
                  <th className="text-center p-4 text-blue-400">{t('premium')}</th>
                  <th className="text-center p-4 text-amber-400">{t('pro')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-800">
                  <td className="p-4 text-slate-300">{t('maximumGuests')}</td>
                  <td className="text-center p-4 text-slate-400">30</td>
                  <td className="text-center p-4 text-slate-300">{t('unlimited')}</td>
                  <td className="text-center p-4 text-slate-300">{t('unlimited')}</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="p-4 text-slate-300">{t('colorThemes')}</td>
                  <td className="text-center p-4 text-slate-400">{t('defaultOnly')}</td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="p-4 text-slate-300">{t('customMessages')}</td>
                  <td className="text-center p-4 text-slate-500">—</td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="p-4 text-slate-300">{t('backgroundImages')}</td>
                  <td className="text-center p-4 text-slate-500">—</td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="p-4 text-slate-300">{t('presetThemes')}</td>
                  <td className="text-center p-4 text-slate-500">—</td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="p-4 text-slate-300">{t('qrCode')}</td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="p-4 text-slate-300">{t('customQrColor')}</td>
                  <td className="text-center p-4 text-slate-500">—</td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="p-4 text-slate-300">Floor Plan Editor</td>
                  <td className="text-center p-4 text-slate-500">—</td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="p-4 text-slate-300">Love Gallery</td>
                  <td className="text-center p-4 text-slate-500">—</td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
